/**
 * Onboarding — where a customer stands on the way to being allowed to hold
 * money, and what is still outstanding.
 *
 * # The states and evidence types below are NOT invented
 *
 * They are transcribed from `go-wallet/internal/onboarding/acceptance.go` and
 * `welcome.go`, which is the authority. That matters because I have already
 * typed endpoints into `api.ts` that turned out not to exist; the vocabulary
 * here at least matches the domain even though the transport does not exist yet.
 *
 * # Three states that look alike on screen and are not alike
 *
 * `GET /v1/onboarding` (`go-wallet#287`) answers for a customer. Before there
 * is a customer it answers `403 not_a_customer`, and if it cannot answer at all
 * it fails. Those are three different facts:
 *
 *   `unregistered`  signed in, no wallet yet — nobody has called
 *                   `POST /v1/registrations`. The remedy is a form.
 *   `ready`         the backend told us where the customer stands.
 *   `unavailable`   we could not find out. The remedy is to ask again.
 *
 * The difference matters more here than almost anywhere else in this app.
 * "We have not started your checks" and "we cannot tell you where your checks
 * stand" look identical in a status list and mean opposite things about what
 * the customer should do next. Defaulting the unknown case to `pending` would
 * invite a customer to redo work they have already done.
 *
 * # What this module must never do
 *
 * **Decide anything.** Invariant 7 makes country gating fail-closed and it is
 * the boundary of the registered mandate; invariant 8 puts every authorisation
 * through one server-side seam. This renders backend state. A client-side
 * "you look eligible" is an authorization decision in a browser bundle, and a
 * customer acting on it would be refused later with no explanation.
 */

import { isNotACustomer } from '@/lib/api'
import type { WalletApi } from '@/lib/api'

/** `onboarding.State` in the backend. */
export const ACCEPTANCE_STATES = [
  'pending',
  'in_review',
  'more_info_required',
  'accepted',
  'rejected',
] as const
export type AcceptanceState = (typeof ACCEPTANCE_STATES)[number]

/** `onboarding.EvidenceType`. */
export const EVIDENCE_TYPES = [
  'passport',
  'national_id',
  'driving_licence',
  'residence_permit',
  'proof_of_address',
  'company_registry_extract',
  'ubo_declaration',
  'director_id',
  'selfie_liveness',
] as const
export type EvidenceType = (typeof EVIDENCE_TYPES)[number]

/** `onboarding.EvidenceStatus`. */
export type EvidenceStatus = 'pending' | 'verified' | 'rejected' | 'expired'

export interface Evidence {
  /**
   * OPEN, not the `EvidenceType` union (#100).
   *
   * The contract publishes it as `{ type: string }` with no enum. Typing it as
   * the union here would put the closed set back one layer up and invite the
   * same refusal to return. Use `evidenceLabel()` to render it.
   */
  readonly type: string
  readonly status: EvidenceStatus
  /** Ours, never the vendor's text (backend Part III). Absent when there is none. */
  readonly reason?: string
  readonly issuingCountry?: string
  readonly expiresAt?: string
}

/**
 * Who the next move belongs to.
 *
 * The operator SLA queue models this distinction already, for exactly the
 * reason it matters here: a customer told "in review" who actually needs to
 * upload a document will wait indefinitely, and a queue of rows nobody can move
 * is one an analyst works top-down for a morning without helping anyone.
 */
export type WaitingOn = 'customer' | 'institution' | 'nobody'

export interface Onboarding {
  readonly state: AcceptanceState
  readonly waitingOn: WaitingOn
  /**
   * The human sentence for this state, from the backend's state machine.
   *
   * Rendered in preference to anything this app could compose, because the
   * backend carries it precisely "so there is one wording rather than one per
   * client" — and a wallet, an iOS app and an Android app disagreeing about what
   * "more info required" means is how a customer gets three different answers.
   */
  readonly blocking: string
  readonly evidence: readonly Evidence[]
  /** Whether the backend says a verification session can be started now. */
  readonly canStartVerification: boolean
  /**
   * A live vendor session's URL, when one is running.
   *
   * Present so a customer who closed the tab continues rather than starting
   * again — restarting would create a second vendor session for one person.
   */
  readonly verificationUrl?: string
}

export type OnboardingState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly onboarding: Onboarding }
  /**
   * Signed in, and not a customer yet.
   *
   * A real, expected, extremely common state — every person who has ever signed
   * up passes through it — and not an error. It is separate from `ready` because
   * there is no onboarding to report yet, and separate from `unavailable`
   * because we know exactly what is missing and exactly how to fix it.
   */
  | { readonly status: 'unregistered' }
  /** We could not find out. NOT the same as "not started". */
  | { readonly status: 'unavailable'; readonly reason: string }

/** Human labels. Ours, never a vendor's reason string (backend Part III). */
export const STATE_LABEL: Record<AcceptanceState, string> = {
  pending: 'Not started',
  in_review: 'Being checked',
  more_info_required: 'We need something else',
  accepted: 'Verified',
  rejected: 'Not approved',
}

export const EVIDENCE_LABEL: Record<EvidenceType, string> = {
  passport: 'Passport',
  national_id: 'National ID card',
  driving_licence: 'Driving licence',
  residence_permit: 'Residence permit',
  proof_of_address: 'Proof of address',
  company_registry_extract: 'Company registry extract',
  ubo_declaration: 'Ownership declaration',
  director_id: 'Director ID',
  selfie_liveness: 'A photo of you',
}

/**
 * What to call an evidence type on screen.
 *
 * Falls back to the backend's own token for a type this bundle has not shipped a
 * label for. Ugly, and the alternatives are worse: dropping the row hides a
 * requirement, and refusing the response threw away an accepted customer's whole
 * onboarding state (#100). A customer who sees an unfamiliar token still sees
 * that something is outstanding, which is the fact that matters.
 */
export function evidenceLabel(type: string): string {
  return EVIDENCE_LABEL[type as EvidenceType] ?? type
}

const EVIDENCE_STATUSES = ['pending', 'verified', 'rejected', 'expired'] as const

function isEvidenceStatus(v: unknown): v is EvidenceStatus {
  return typeof v === 'string' && (EVIDENCE_STATUSES as readonly string[]).includes(v)
}

/** Whether the customer may use the wallet proper. */
export function isOnboarded(o: Onboarding): boolean {
  return o.state === 'accepted'
}

interface EvidenceDTO {
  type: string
  status: string
  reason?: string
  issuing_country?: string
  expires_at?: string
}

interface OnboardingDTO {
  state: string
  waiting_on: string
  blocking: string
  evidence: EvidenceDTO[]
  can_start_verification: boolean
  verification_url?: string
}

function isState(v: string): v is AcceptanceState {
  return (ACCEPTANCE_STATES as readonly string[]).includes(v)
}
/**
 * Read onboarding state (`GET /v1/onboarding`).
 *
 * Decodes defensively rather than casting. The backend will grow vocabulary this
 * bundle predates — a browser bundle can be months old — and the two cases are
 * NOT handled the same way (#100):
 *
 *   an unknown `state`         FATAL. `WalletHome` routes on it, so a value we
 *                              cannot place makes the routing undecidable.
 *   an unknown evidence type   RENDERED, labelled with the backend's own token.
 *                              Nothing branches on it, so refusing the whole
 *                              response bought nothing and cost an accepted
 *                              customer their wallet home.
 *
 * That is the line, and it is the same one `accounts.ts` draws: branch on it,
 * refuse it; label only, render it.
 */
export async function fetchOnboarding(api: WalletApi): Promise<OnboardingState> {
  let dto: OnboardingDTO
  try {
    dto = await api.get<OnboardingDTO>('/v1/onboarding')
  } catch (err) {
    if (isNotACustomer(err)) {
      // Signed in, no wallet yet.
      //
      // This used to synthesise a `pending` onboarding here and explain it with
      // "Confirm your email address to open your wallet." Nothing links an
      // identity to a wallet on email verification — `POST /v1/registrations`
      // does, and no client was calling it. So the app read the state correctly
      // and then narrated a cause nobody had verified, which is the same defect
      // as `#17` and sent the customer to do something that could not help
      // (`#22`).
      //
      // The honest answer is a state of its own, and the screen that owns it
      // offers the thing that actually moves it.
      return { status: 'unregistered' }
    }
    return {
      status: 'unavailable',
      reason: err instanceof Error ? err.message : String(err),
    }
  }

  if (!isState(dto.state)) {
    return {
      status: 'unavailable',
      reason: `the backend reported a state this app does not know (${dto.state})`,
    }
  }

  const evidence: Evidence[] = []
  for (const e of dto.evidence ?? []) {
    // AN UNKNOWN EVIDENCE TYPE NO LONGER DISCARDS THE WHOLE READ (#100).
    //
    // This used to return `unavailable` for any type absent from
    // `EVIDENCE_TYPES`, on the reasoning that dropping a requirement would show
    // a shorter checklist than reality. The reasoning is right; refusing the
    // ENTIRE response was not the way to honour it, and it fired regardless of
    // status or state.
    //
    // The contract publishes this field as `{ type: string }` with NO enum, in
    // a document whose stated reason for existing is that its consumers are
    // binaries that cannot be updated. So one additive member on the backend —
    // its own CI green — made every deployed bundle simultaneously discard good
    // 200s. `WalletHome` routes `unavailable` as "not accepted", so an ACCEPTED
    // customer with an issued IBAN was redirected off `/` onto a screen saying
    // "We cannot tell you where your checks stand" about facts the backend had
    // just supplied.
    //
    // Nothing branches on this value — it selects a label. So the checklist
    // stays complete by keeping the row and labelling it with the backend's own
    // token, which is honest and is strictly more than either alternative gave.
    // Compare `accounts.ts`, which argues the same way for the same shape, and
    // `state` above, which stays fatal BECAUSE routing branches on it. That is
    // the line: branch on it, refuse it; label only, render it.
    evidence.push({
      type: e.type,
      // Unknown statuses count as outstanding, which is the safe direction: a
      // row we cannot classify appears in the "still to do" count rather than
      // silently completing the checklist.
      status: isEvidenceStatus(e.status) ? e.status : 'pending',
      ...(e.reason === undefined ? {} : { reason: e.reason }),
      ...(e.issuing_country === undefined ? {} : { issuingCountry: e.issuing_country }),
      ...(e.expires_at === undefined ? {} : { expiresAt: e.expires_at }),
    })
  }

  const waitingOn: WaitingOn =
    dto.waiting_on === 'customer' || dto.waiting_on === 'institution' ? dto.waiting_on : 'nobody'

  return {
    status: 'ready',
    onboarding: {
      state: dto.state,
      waitingOn,
      blocking: dto.blocking,
      evidence,
      canStartVerification: dto.can_start_verification === true,
      ...(dto.verification_url === undefined ? {} : { verificationUrl: dto.verification_url }),
    },
  }
}

interface StartSessionDTO {
  url: string
  expires_at?: string
  started: boolean
}

/**
 * Start an identity verification session (`POST /v1/verification-sessions`).
 *
 * Returns the vendor's hosted URL for the browser to navigate to. The customer
 * captures documents there and the vendor webhooks the verdict to the backend —
 * this app is not in that path and must never treat the return as a result.
 *
 * The idempotency key is caller-supplied and stable for one intent (invariant
 * 5): a double-click must not create two vendor sessions for one person. The
 * backend distinguishes "I created this" from "this was already running", so a
 * retry resumes rather than duplicates.
 */
export async function startVerification(
  api: WalletApi,
  idempotencyKey: string,
  redirectUrl: string,
): Promise<string> {
  const dto = await api.post<StartSessionDTO>(
    '/v1/verification-sessions',
    { redirect_url: redirectUrl },
    { idempotencyKey },
  )
  if (typeof dto?.url !== 'string' || dto.url === '') {
    throw new Error('the backend started a verification session but returned no URL')
  }
  return dto.url
}
